import { useEffect, useLayoutEffect, useRef, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, animate as fmAnimate } from "framer-motion";
import { Copy, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

function StandaloneEscape({ url, bg }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback: select the text manually
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      } catch {
        /* noop */
      }
      el.remove();
    }
  };

  return (
    <div
      className="fixed inset-0 w-full h-full flex flex-col items-center justify-center px-6 gap-4"
      style={{ backgroundColor: bg || "#0A0A0A" }}
      data-testid="public-standalone-escape"
    >
      <div className="text-white/80 text-center text-sm max-w-xs">
        Open this link in your browser to view the form.
      </div>
      <div
        className="w-full max-w-sm h-12 rounded-md bg-white/95 text-zinc-900 text-xs font-mono px-3 inline-flex items-center truncate"
        data-testid="public-standalone-url"
      >
        {url}
      </div>
      <button
        type="button"
        onClick={onCopy}
        className="inline-flex items-center justify-center h-14 px-8 rounded-full bg-white text-zinc-900 text-base font-semibold shadow-xl active:scale-[0.98] transition-transform gap-2"
        data-testid="public-copy-link-btn"
      >
        {copied ? (
          <>
            <Check className="w-4 h-4" /> Copied
          </>
        ) : (
          <>
            <Copy className="w-4 h-4" /> Copy Link
          </>
        )}
      </button>
    </div>
  );
}

// Each height: dvh + a small fixed pixel bump per user spec.
function heightCss(h) {
  if (h === "1/4") return "calc(25dvh + 10px)";
  if (h === "2/4") return "calc(50dvh + 20px)";
  if (h === "3/4") return "calc(75dvh + 30px)";
  return "calc(80dvh + 40px)"; // 4/5
}

function heightToDvhNumber(h) {
  if (h === "1/4") return 25;
  if (h === "2/4") return 50;
  if (h === "3/4") return 75;
  return 80;
}

function setOrCreateMeta(name, content) {
  if (!content) return null;
  let el = document.querySelector(`meta[name="${name}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  const previous = el.getAttribute("content");
  el.setAttribute("content", content);
  return { el, created, previous };
}

function loadGoogleFont(family) {
  if (!family) return null;
  const id = `gf-${family.replace(/\s+/g, "-")}`;
  if (document.getElementById(id)) return null;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
    family
  )}:wght@400;500;600;700;800;900&display=swap`;
  document.head.appendChild(link);
  return link;
}

export default function PublicPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [error, setError] = useState("");
  const [dismissing, setDismissing] = useState(false);

  const popupY = useMotionValue(0);
  const dragStartRef = useRef(null);
  const dismissingRef = useRef(false);
  const [standalone, setStandalone] = useState(false);

  useEffect(() => {
    const isStandalone =
      (typeof window !== "undefined" &&
        ((window.navigator && window.navigator.standalone === true) ||
          (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches)));
    setStandalone(Boolean(isStandalone));
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/public/pages/${slug}`);
        if (active) setPage(data);
      } catch (e) {
        if (active) setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
    return () => {
      active = false;
    };
  }, [slug]);

  // Theme color + iOS PWA prevention + disable pull-to-refresh
  // useLayoutEffect runs synchronously before browser paint so iOS Safari
  // is more likely to pick up the dynamic theme-color value.
  useLayoutEffect(() => {
    if (!page) return;
    const tc = page.theme_color || null; // strictly the user-set theme; no bg_color fallback
    const themeMeta = tc ? setOrCreateMeta("theme-color", tc) : null;
    const appleCap = setOrCreateMeta("apple-mobile-web-app-capable", "no");
    const mobileCap = setOrCreateMeta("mobile-web-app-capable", "no");
    const appleBar = setOrCreateMeta("apple-mobile-web-app-status-bar-style", "default");

    document.documentElement.classList.add("public-no-bounce");
    document.body.classList.add("public-no-bounce");

    let fontLink = null;
    if (page.header_font) fontLink = loadGoogleFont(page.header_font);

    return () => {
      if (themeMeta) {
        if (themeMeta.created) themeMeta.el.remove();
        else if (themeMeta.previous != null) themeMeta.el.setAttribute("content", themeMeta.previous);
      }
      [appleCap, mobileCap, appleBar].forEach((m) => {
        if (!m) return;
        if (m.created) m.el.remove();
        else if (m.previous != null) m.el.setAttribute("content", m.previous);
      });
      document.documentElement.classList.remove("public-no-bounce");
      document.body.classList.remove("public-no-bounce");
      if (fontLink && fontLink.parentNode) fontLink.parentNode.removeChild(fontLink);
    };
  }, [page]);

  const triggerDismiss = (redirectUrl) => {
    if (dismissingRef.current) return;
    dismissingRef.current = true;
    setDismissing(true);
    window.setTimeout(() => {
      window.location.href = redirectUrl || "https://www.gslitigation.com";
    }, 550);
  };

  // Gestures on the background-above-popup area
  const onBgPointerDown = (e) => {
    if (dismissingRef.current) return;
    dragStartRef.current = { y: e.clientY, t: performance.now() };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onBgPointerMove = (e) => {
    if (!dragStartRef.current) return;
    const dy = e.clientY - dragStartRef.current.y;
    if (dy > 0) popupY.set(dy);
  };
  const onBgPointerUp = (e) => {
    if (!dragStartRef.current) return;
    const dy = e.clientY - dragStartRef.current.y;
    const dt = Math.max(1, performance.now() - dragStartRef.current.t);
    const velocity = (dy / dt) * 1000; // px/s
    dragStartRef.current = null;
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
    if (dy > 90 || velocity > 500) {
      triggerDismiss(page?.redirect_url);
    } else {
      fmAnimate(popupY, 0, { type: "spring", damping: 28, stiffness: 240 });
    }
  };

  const heightInfo = useMemo(() => {
    if (!page) return { css: "calc(50dvh + 20px)", dvh: 50 };
    return { css: heightCss(page.popup_height), dvh: heightToDvhNumber(page.popup_height) };
  }, [page]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6" data-testid="public-error">
        <div className="text-center max-w-sm">
          <div className="text-xs font-bold uppercase tracking-[0.28em] text-zinc-500 mb-3">
            Not found
          </div>
          <h1
            className="text-3xl font-black tracking-tighter text-zinc-900"
            style={{ fontFamily: "var(--font-cabinet), Cabinet Grotesk, sans-serif" }}
          >
            This page doesn’t exist.
          </h1>
          <p className="mt-3 text-zinc-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!page) {
    return (
      <div
        className="min-h-screen w-full"
        style={{ backgroundColor: "#0A0A0A" }}
        data-testid="public-loading"
      />
    );
  }

  if (standalone) {
    // The user opened this URL from an iOS Home-Screen icon saved while the
    // page was standalone-capable. iOS pins that preference for the icon, so
    // the cleanest escape is to give them the link they can paste into Safari.
    const url = typeof window !== "undefined" ? window.location.href : "";
    return (
      <StandaloneEscape url={url} bg={page.bg_color} />
    );
  }

  const headerMode = page.header_mode || "none";
  const headerAlign = page.header_align || "center";
  const headerSize = Number(page.header_size || 64);
  const headerColor = page.header_color || "#FFFFFF";
  const headerFont = page.header_font || null;
  const logoOffset = Number(page.logo_offset || 0);
  const logoOffsetX = Number(page.logo_offset_x || 0);

  const justify =
    headerAlign === "left" ? "flex-start" : headerAlign === "right" ? "flex-end" : "center";
  const textAlign = headerAlign;

  const fontStack = headerFont
    ? `"${headerFont}", "Cabinet Grotesk", ui-sans-serif, system-ui, sans-serif`
    : "var(--font-cabinet), Cabinet Grotesk, ui-sans-serif, system-ui, sans-serif";

  return (
    <>
    <motion.div
      animate={{ opacity: dismissing ? 0 : 1 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-0 w-full overflow-hidden"
      style={{ backgroundColor: page.bg_color, touchAction: "none" }}
      data-testid="public-page"
    >
      {/* BACKGROUND DRAG ZONE (above the popup). Swipe down here to dismiss. */}
      <div
        onPointerDown={onBgPointerDown}
        onPointerMove={onBgPointerMove}
        onPointerUp={onBgPointerUp}
        onPointerCancel={onBgPointerUp}
        className="absolute inset-x-0 top-0 z-0 flex items-center px-6 sm:px-12 select-none"
        style={{
          bottom: heightInfo.css,
          justifyContent: justify,
          touchAction: "none",
          cursor: "grab",
        }}
        data-testid="public-bg-zone"
        aria-label="Swipe down to dismiss"
      >
        {headerMode === "name" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 5, ease: [0.22, 1, 0.36, 1] }}
            className="font-black tracking-tighter leading-none w-full pointer-events-none"
            style={{
              fontFamily: fontStack,
              fontSize: `${headerSize}px`,
              color: headerColor,
              textAlign,
              transform: `translate(${logoOffsetX}px, ${logoOffset}px)`,
            }}
            data-testid="public-bg-title"
          >
            {page.title || ""}
          </motion.div>
        )}
        {headerMode === "logo" && page.logo_url && (
          <motion.img
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 5, ease: [0.22, 1, 0.36, 1] }}
            src={page.logo_url}
            alt={page.title || page.slug}
            className="pointer-events-none"
            style={{
              width: `${headerSize}px`,
              maxWidth: "96%",
              height: "auto",
              transform: `translate(${logoOffsetX}px, ${logoOffset}px)`,
              objectFit: "contain",
            }}
            data-testid="public-bg-logo"
          />
        )}
      </div>
    </motion.div>

    <AnimatePresence>
      {!dismissing && (
        <motion.div
          key="popup"
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          style={{ height: heightInfo.css, y: popupY, touchAction: "auto", bottom: -30 }}
          className="fixed bottom-0 left-0 right-0 z-10 bg-white rounded-t-[28px] overflow-hidden shadow-[0_-20px_60px_rgba(0,0,0,0.25)]"
          data-testid="public-popup"
        >
          <iframe
            title={page.title || page.slug}
            src={page.target_url}
            className="absolute inset-0 w-full h-full border-0"
            allow="clipboard-write; clipboard-read; payment; encrypted-media; geolocation"
            data-testid="popup-iframe"
          />
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
}
